<script setup>
import {computed, onMounted, ref} from "vue";
import {useStore} from "vuex";
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const store = useStore()

const props = defineProps(['isOpen', 'initialsSelected'])
const emit = defineEmits(['close', 'update'])

const loading = ref(true)
const search = ref('')
const selected = ref([])
const filter = ref(false)
const errorMessages = ref([])

const academics = computed(() => {
  const list = [
    ...store.getters["Subjects/getSubject"].subjectUsers,
    ...store.getters["Academics/getAcademics"],
  ]
  return filter.value ?
      selected.value :
      // removing duplicity
      Array.from(new Map(list.map(user => [user.uisId, user])).values())
})


const headersAcademics = [
  { key: 'fullName', title: t('gamification.fullname'), width: "40%" },
  { key: 'uisId', title: 'UIS Id', width: "20%"},
  { key: 'login', title: 'xlogin', sortable: false, width: "40%" },
]

onMounted(() => selected.value = props.initialsSelected)

async function fetchData(){
  loading.value = true
  await store.dispatch('Academics/fetchAllAcademics')
  selected.value = [...store.getters["Subjects/getSubject"].subjectUsers]
  loading.value = false
}

async function createGamificationProfile(userItem){
  if(userItem.login && userItem.login.trim()){
    loading.value = true
    try {
      const res = await store.dispatch('user/fetchByLoginOrId',{xlogin: userItem.login.trim()})
      const academic = res.data.content
      if(academic.uisId !== userItem.uisId){
        updateErrorMessages('UPDATE',{
          uisId: userItem.uisId,
          message: t('gamification.academics_ids_error')
        })
      } else {
        updateErrorMessages('REMOVE', {uisId: userItem.uisId, message: ''})
        setLogin(userItem.uisId, academic.login)
      }
    } catch (e) {
      handleErrors(e, userItem)
    } finally {
      loading.value = false
    }
  }
}

function handleErrors(e, userItem){
  if(e.statusCode === 404){
    updateErrorMessages('UPDATE',{
      uisId: userItem.uisId,
      message: t('gamification.login_error')
    })
  } else {
    updateErrorMessages('UPDATE',{
      uisId: userItem.uisId,
      message: t('gamification.general_error')
    })
  }
}

function setLogin(uisId, login){
  store.dispatch('Subjects/updateSubjectUserLogin', {uisId: uisId, login: login})
  const index = academics.value.findIndex(a => a.uisId === uisId)
  academics.value[index].active = true
  academics.value[index].login = login
}

function update(){
  emit('update', {users: selected.value})
  emit('close')
}

function updateSelected(users){
  selected.value = [...users]
}

function close(){
  emit('close')
  selected.value = [...store.getters["Subjects/getSubject"].subjectUsers]
}

function isSelectable(item){
  return item.uisId !== store.getters['user/uisId']
}

function updateErrorMessages(operation, errorMessage){
  if(operation === 'REMOVE'){
    errorMessages.value = errorMessages.value.filter(em => em.uisId !== errorMessage.uisId)
  }
  if(operation === 'UPDATE'){
    const index = errorMessages.value.findIndex(em => em.uisId === errorMessage.uisId)
    if(index === -1){
      errorMessages.value.push(errorMessage)
    } else {
      errorMessages.value[index].message = errorMessage.message
    }
  }
}

function getErrorMessage(uisId){
  const em = errorMessages.value.find(em => em.uisId === uisId)
  if(em){
    return em.message
  } else {
    return ''
  }
}



function updateItems(items){
  selected.value = items
  // refresh text field after unselect academic without active gamification account
  academics.value.forEach((a, indexA) => {
    if(a.active === false){
      const indexS = items.findIndex(s => s.uisId === a.uisId)
      if(indexS === -1){
        academics.value[indexA].login = ''
        const indexM = errorMessages.value.findIndex(em => em.uisId === a.uisId)
        if(indexM !== -1){
          updateErrorMessages('REMOVE', {uisId: errorMessages.value[indexM].uisId, message: ''})
        }
      }
    }
  })
}


defineExpose({
  updateSelected
})

</script>

<template>
  <v-dialog
      :model-value="props.isOpen"
      @update:model-value="value => !value && close()"
      @after-enter="fetchData"
      max-width="1200"
      scrollable >
    <v-card>

      <template v-slot:prepend>
        <div class="text-h5">{{$t('gamification.add_academic')}}</div>
        <v-chip v-if="selected.length > 0" class="ml-4"> {{ selected.length }} {{$t('gamification.selected')}}</v-chip>
      </template>
      <template v-slot:append>
        <v-icon @click="close()">close</v-icon>
      </template>
      <div class="d-flex align-center ga-2 px-5">
        <v-text-field
            v-model="search"
            :label="t('gamification.search_academics')"
            prepend-inner-icon="search"
            variant="outlined"
            density="compact"
            hide-details
            single-line/>
        <v-switch
            class="mr-12 ml-4"
            v-model="filter"
            :label="t('gamification.show_academics_selected')"
            color="primary"
            hide-details
        />
      </div>
      <v-data-table
          :model-value="selected"
          @update:model-value="updateItems"
          :items="academics"
          :loading="loading"
          :headers="headersAcademics"
          :search="search"
          :loading-text="t('gamification.loading')"
          :no-data-text="t('gamification.no_data')"
          show-select
          class="px-5 pt-1"
          return-object
          height="400"
          fixed-header
          :item-selectable="isSelectable"
      >
        <template v-slot:item.login="{ item }">
          <div v-if="item.active">
            {{ item.login }}
          </div>
          <div v-else>
            <div
                v-if="selected.find(s => s.uisId === item.uisId)"
                class="d-flex justify-center align-center ga-2"
            >
              <v-text-field
                  v-model="item.login"
                  :error-messages="getErrorMessage(item.uisId)"
                  :class="`pt-2 ${getErrorMessage(item.uisId).length > 0 && 'pb-2'}`"
                  :disabled="loading"
                  :placeholder="t('gamification.enter_login')" variant="outlined" density="compact" />
              <v-btn
                  @click="createGamificationProfile(item)"
                  :loading="loading"
                  class="mb-2" variant="text" size="small" color="primary">{{$t('gamification.add')}}</v-btn>
            </div>
            <div v-else>
              <v-tooltip
                  max-width="300"
                  :text="t('gamification.academic_gam_profile_info')"
                  location="top">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props" icon="warning" color="amber-darken-1" />
                </template>
              </v-tooltip>
            </div>
          </div>
        </template>
      </v-data-table>
      <v-card-actions>
        <v-btn
            @click="update"
            color="primary"
            variant="flat"
            :disabled="selected.length === 0 || selected.find(s => s.active === false)"
        >
          {{$t('gamification.save')}}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>

</style>